import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Hash, CheckCircle2 } from 'lucide-react';
import { Button } from '@shared/ui/primitives/Button';
import { TextField } from '@shared/ui/primitives/TextField';
import { Field } from '@shared/ui/primitives/Field';
import { Banner } from '@shared/ui/feedback/Banner';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { CountryCodePicker, findCountry, listCountries } from '@shared/ui/primitives/CountryCodePicker';
import { buildE164 } from '@shared/lib/phone';
import { useApiForm } from '@shared/hooks/useApiForm';
import { useModuleStore } from '@shared/stores/moduleStore';
import { ResetSchema } from './schema';
import { resetPassword } from './api';

/**
 * Reset-password — OTP-based reset that honours the tenant's
 * `authConfig.allowed_identifiers`. The user arrives here with
 * `?method=email|mobile&identifier=…` query params populated by the
 * `ForgotPassword` screen, enters the 6-digit OTP they just received
 * (over email or SMS, depending on the channel), and chooses a new
 * password. One atomic `resetPassword` call verifies the OTP and writes
 * the password — there's no separate "verify OTP" step. On success the
 * user is sent to `/login` to sign in.
 */
export function ResetPassword(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [done, setDone] = useState(false);
  const authConfig = useModuleStore((s) => s.config?.authConfig);

  const allowedIdentifiers = authConfig?.allowed_identifiers ?? 'both';
  const canEmail = allowedIdentifiers !== 'mobile';
  const canMobile = allowedIdentifiers !== 'email';

  // Pre-fill from the forgot-password navigation. Falls back to the
  // first allowed channel if the query string is missing/inconsistent.
  const initialMethod = useMemo<'email' | 'mobile'>(() => {
    const fromQuery = params.get('method');
    if (fromQuery === 'mobile' && canMobile) return 'mobile';
    if (fromQuery === 'email' && canEmail) return 'email';
    return canEmail ? 'email' : 'mobile';
  }, [params, canEmail, canMobile]);

  const [method, setMethod] = useState<'email' | 'mobile'>(initialMethod);
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('ZA');
  const [localMobile, setLocalMobile] = useState('');

  // Prefill from the query string on mount. For mobile we split the E.164
  // value back into a dial-code + local part so the CountryCodePicker
  // shows the right country.
  useEffect(() => {
    const ident = params.get('identifier') ?? '';
    if (!ident) return;
    if (initialMethod === 'email') {
      setEmail(ident);
      return;
    }
    // Best-effort E.164 split: dial codes already carry the leading `+`.
    // Pick the longest match so e.g. `+1242` (Bahamas) wins over `+1` (US).
    const cleaned = ident.startsWith('+') ? ident : `+${ident}`;
    const match = [...listCountries()]
      .filter((c) => cleaned.startsWith(c.dialCode))
      .sort((a, b) => b.dialCode.length - a.dialCode.length)[0];
    if (match) {
      setCountryCode(match.code);
      setLocalMobile(cleaned.slice(match.dialCode.length));
    } else {
      setLocalMobile(cleaned);
    }
  }, [params, initialMethod]);

  const dialCode = findCountry(countryCode).dialCode;
  const mobileE164 = buildE164(dialCode, localMobile);
  const identifier = method === 'mobile' ? mobileE164 : email.trim();

  // `identifier` + `method` live in local state (driven by the channel
  // toggle); OTP/password/confirm are RHF-registered fields. We keep
  // RHF in sync so `handleSubmit` validates the full schema (it still
  // surfaces field-level errors on otp/password/confirm in the UI),
  // and we re-validate the merged payload defensively before calling
  // the API.
  const form = useApiForm({
    schema: ResetSchema,
    defaultValues: { method: initialMethod, identifier: '', otp: '', password: '', confirm: '' },
  });

  // Mirror the local identifier/method into RHF on every change so the
  // RHF `handleSubmit` validator sees the live values when the user
  // clicks Save.
  useEffect(() => {
    form.setValue('method', method, { shouldValidate: false });
  }, [method, form]);
  useEffect(() => {
    form.setValue('identifier', identifier, { shouldValidate: false });
  }, [identifier, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    const merged = {
      method,
      identifier,
      otp: values.otp,
      password: values.password,
      confirm: values.confirm,
    };
    const parsed = ResetSchema.safeParse(merged);
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      form.setServerMessage(first?.message ?? 'Check your details and try again.');
      return;
    }
    const ok = await form.run(() => resetPassword(parsed.data));
    if (ok === true) setDone(true);
  });

  if (done) {
    return (
      <EmptyState
        icon={<CheckCircle2 className="h-7 w-7 text-success" strokeWidth={1.5} />}
        title="Password updated."
        description="Sign in with your new password to continue."
        action={
          <Button variant="primary" onClick={() => navigate('/login', { replace: true })}>
            Sign in
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <h1 className="font-display text-display-xl text-ink leading-tight">Choose a new password.</h1>
        <p className="text-body text-ink-secondary">
          Enter the 6-digit code we just sent you{method === 'mobile' ? ' by SMS' : ' by email'} along with your
          new password.
        </p>
      </header>

      {form.serverMessage ? (
        <Banner
          variant="danger"
          title="Couldn't reset"
          description={form.serverMessage}
          onDismiss={() => form.setServerMessage(null)}
          className="-mx-6 rounded-md"
        />
      ) : null}

      {/* Channel toggle — only renders when both channels are allowed. */}
      {canEmail && canMobile ? (
        <div
          className="inline-flex self-start rounded-pill bg-sunken p-1 text-body-sm"
          role="tablist"
          aria-label="OTP channel"
        >
          <button
            type="button"
            role="tab"
            aria-selected={method === 'email'}
            className={`rounded-pill px-3 py-1.5 font-medium transition-colors ${
              method === 'email' ? 'bg-paper text-ink shadow-sm' : 'text-ink-secondary'
            }`}
            onClick={() => setMethod('email')}
          >
            Email
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={method === 'mobile'}
            className={`rounded-pill px-3 py-1.5 font-medium transition-colors ${
              method === 'mobile' ? 'bg-paper text-ink shadow-sm' : 'text-ink-secondary'
            }`}
            onClick={() => setMethod('mobile')}
          >
            Mobile
          </button>
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        {method === 'email' ? (
          <Field
            label="Email"
            type="email"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="off"
            autoCorrect="off"
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
          />
        ) : (
          <div className="flex flex-col gap-1">
            <label className="text-body-sm font-medium text-ink">Mobile</label>
            <div className="flex gap-2">
              <CountryCodePicker
                value={countryCode}
                onChange={(c) => setCountryCode(c.code)}
              />
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                placeholder="82 123 4567"
                value={localMobile}
                onChange={(event) => setLocalMobile(event.target.value.replace(/\D/g, '').slice(0, 15))}
                className="h-12 w-full rounded-md border border-hairline-strong bg-elevated px-3.5 text-body text-ink placeholder:text-ink-muted focus:border-primary-600 focus:outline-none"
              />
            </div>
          </div>
        )}
        <TextField
          label="6-digit code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          leadingIcon={<Hash className="h-4 w-4" strokeWidth={1.5} />}
          helper="Codes expire after 15 minutes."
          error={form.formState.errors.otp?.message}
          {...form.register('otp')}
        />
        <TextField
          label="New password"
          type="password"
          autoComplete="new-password"
          leadingIcon={<Lock className="h-4 w-4" strokeWidth={1.5} />}
          helper="At least 8 characters with upper, lower, number, and a symbol."
          error={form.formState.errors.password?.message}
          {...form.register('password')}
        />
        <TextField
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          leadingIcon={<Lock className="h-4 w-4" strokeWidth={1.5} />}
          error={form.formState.errors.confirm?.message}
          {...form.register('confirm')}
        />
        <Button type="submit" variant="primary" size="lg" width="full" loading={form.isPending}>
          Save password
        </Button>
      </form>

      <p className="text-body-sm text-ink-secondary text-center">
        <Link to="/forgot-password" className="text-primary-700 font-semibold hover:underline">
          Resend code
        </Link>
        {' · '}
        <Link to="/login" className="text-primary-700 font-semibold hover:underline">
          Back to sign in
        </Link>
      </p>
    </div>
  );
}
